import { useEffect, useMemo, useRef, useState } from 'react'
import { Send, RefreshCw, Search, ExternalLink, Zap, Plus, Trash2, X,
         Paperclip, FileText, Film, Mic, Download, Smile, AlertCircle, Clock, Bell } from 'lucide-react'
import { api } from '../lib/api'
import { resizeImageFile } from '../lib/imageResize'

/** Matches functions/api/_bot_stickers.js — read the note there before changing
 *  it. 192 since 2026-09-16 (was 320, LINE's own size; too big in the thread). */
const STICKER_DIM = 192

/**
 * /bot — the chatbot inbox.
 *
 * WHO ANSWERS is bot_session.status. Sending a reply from here flips it to
 * 'human' automatically, because LINE gives us no signal when staff reply in
 * LINE OA Manager — so replying HERE is the only moment we can observe a takeover.
 * Answering in OA Manager instead leaves the bot talking over the top of you.
 *
 * But status='human' does NOT mean a person is on it — the bot writes the same
 * value when it hands off. `taken_over_by` is the field that knows the
 * difference, and the inbox splits the two: รอเจ้าหน้าที่ (red, unclaimed) vs
 * เจ้าหน้าที่ดูแล (amber, owned). See StatusPill.
 */

const POLL_MS = 15000

/** Both inbox filters. Same ring/focus language as the rest of the CRM. */
const FILTER_SEL = 'w-full min-w-0 truncate rounded-md bg-white px-2 py-1.5 text-[12px] ' +
  'text-slate-700 ring-1 ring-slate-200 hover:bg-slate-50 focus:outline-none focus:ring-2 focus:ring-amber-400'

function timeAgo(ts) {
  if (!ts) return ''
  const s = Math.floor(Date.now() / 1000) - ts
  if (s < 60) return 'เมื่อครู่'
  if (s < 3600) return `${Math.floor(s / 60)} นาทีที่แล้ว`
  if (s < 86400) return `${Math.floor(s / 3600)} ชม.ที่แล้ว`
  return `${Math.floor(s / 86400)} วันที่แล้ว`
}

function clock(ts) {
  return new Date(ts * 1000).toLocaleString('th-TH', {
    day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit',
  })
}

/** Where the conversation came from. More channels are coming (Facebook next). */
const CHANNELS = {
  line:     { label: 'LINE',     short: 'LINE', dot: 'bg-[#06C755]', cls: 'text-[#06C755]' },
  facebook: { label: 'Facebook', short: 'FB',   dot: 'bg-[#1877F2]', cls: 'text-[#1877F2]' },
  web:      { label: 'เว็บไซต์',  short: 'เว็บ',  dot: 'bg-slate-400', cls: 'text-slate-500' },
}

/**
 * A dot and two to four characters — in the list. This used to be a filled,
 * ring-bordered badge spelling out "Facebook", and on a 320px row that one word
 * pushed the status pill and the Facebook clock onto a second line, so every
 * row stood two labels tall. The brand colour is the part that identifies the
 * channel at a glance; the word is not.
 *
 * The conversation header has the room to spell it, so it passes size="md".
 */
function ChannelBadge({ channel, size = 'sm' }) {
  const md = size === 'md'
  const c = CHANNELS[channel] || { label: channel || '?', short: channel || '?', dot: 'bg-slate-300', cls: 'text-slate-500' }
  return (
    <span title={c.label}
      className={`inline-flex shrink-0 items-center gap-1 whitespace-nowrap font-semibold ${
        md ? 'text-[11px]' : 'text-[10px]'} ${c.cls}`}>
      <span className={`h-1.5 w-1.5 shrink-0 rounded-full ${c.dot}`} />
      {md ? c.label : c.short}
    </span>
  )
}

/**
 * WHO IS ON THIS ROOM — and `status` alone cannot answer that. The bot writes
 * status='human' whenever it hands off (the model asked, no_progress hit 3, a
 * photo it cannot read arrived, or its own turn threw), and so does a staff
 * member taking over. Only `taken_over_by` tells them apart: the two
 * human-driven writers stamp it, every automatic one leaves it NULL.
 *
 * So status='human' with no name is a QUEUE, not an owner, and it gets its own
 * red pill. Reading it as "someone is handling this" is how six rooms sat
 * unanswered, the oldest for nine days.
 */
function StatusPill({ status, takenOverBy, size = 'sm' }) {
  const map = {
    bot:     ['บอทกำลังตอบ',   'bg-emerald-50 text-emerald-700', 'bg-emerald-500'],
    waiting: ['รอเจ้าหน้าที่',  'bg-rose-50 text-rose-700',       'bg-rose-500'],
    human:   ['เจ้าหน้าที่ดูแล', 'bg-amber-50 text-amber-700',     'bg-amber-500'],
    done:    ['ปิดแล้ว',        'bg-slate-100 text-slate-500',    'bg-slate-400'],
  }
  const key = status === 'human' && !takenOverBy ? 'waiting' : status
  const [label, cls, dot] = map[key] || map.done
  // The lucide icon that used to sit in here said nothing the colour did not,
  // and cost ~16px on a row where the width is the whole problem. A dot carries
  // the same three-way distinction in a quarter of the space.
  return (
    <span className={`inline-flex shrink-0 items-center gap-1 whitespace-nowrap rounded px-1.5 font-medium ${
      size === 'md' ? 'py-0.5 text-[11px]' : 'text-[10px] leading-[18px]'} ${cls}`}>
      <span className={`h-1.5 w-1.5 shrink-0 rounded-full ${dot}`} />{label}
    </span>
  )
}

/**
 * Facebook's 24-hour clock, made visible.
 *
 * Staff had no view of this at all. The composer looked the same at hour 2 and
 * at hour 64, so the first anyone knew was a failed send — on Lead #2382 that
 * was three days after the customer wrote, by which point the window was the
 * least of the problem. A deadline nobody can see is not a deadline.
 *
 * Renders nothing on LINE, and nothing while there is still a comfortable
 * margin: a badge on every Facebook room would be wallpaper within a day.
 */
function FbWindowBadge({ win }) {
  if (!win || win.state === 'open') return null
  const closed = win.state === 'closed'
  return (
    <span
      title={closed
        ? 'เกิน 24 ชม. แล้ว — ตอบได้เฉพาะเมื่อแอปได้รับอนุมัติ Human Agent'
        : 'ใกล้หมดเวลาตอบกลับ 24 ชม. ของ Facebook'}
      className={`inline-flex shrink-0 items-center gap-0.5 whitespace-nowrap rounded px-1.5 text-[10px] font-medium leading-[18px] ${
        closed ? 'bg-rose-600 text-white' : 'bg-amber-100 text-amber-800'}`}>
      <Clock size={10} />{closed ? 'หมดเวลา' : `${win.hours_left} ชม.`}
    </span>
  )
}

/**
 * The two counts that mean somebody has to do something now.
 *
 * They are shortcuts INTO the status dropdown, not a third filter: clicking one
 * sets the same `filter` the dropdown reads, and the chip is lit only while the
 * dropdown is on that value. So there is one answer on screen to "what am I
 * looking at", never two controls disagreeing. Clicking a lit chip clears it.
 *
 * Each one hides itself at zero — a chip that permanently reads 0 teaches
 * people to stop reading it, which is the opposite of why it exists.
 */
function UrgentChip({ active, tone, icon: Icon, label, count, onClick }) {
  const t = tone === 'rose'
    ? { on: 'bg-rose-600 text-white',   off: 'bg-rose-50 text-rose-700 ring-1 ring-rose-200 hover:bg-rose-100',      n: 'bg-rose-600 text-white' }
    : { on: 'bg-amber-500 text-white',  off: 'bg-amber-50 text-amber-800 ring-1 ring-amber-200 hover:bg-amber-100',  n: 'bg-amber-500 text-white' }
  return (
    <button onClick={onClick} title={active ? 'คลิกเพื่อล้างตัวกรอง' : label}
      className={`inline-flex min-w-0 flex-1 items-center justify-center gap-1 rounded-md px-2 py-1 text-[11px] font-medium transition-colors ${
        active ? t.on : t.off}`}>
      <Icon size={11} className="shrink-0" />
      <span className="truncate">{label}</span>
      <span className={`shrink-0 rounded px-1 text-[10px] font-semibold tabular-nums ${
        active ? 'bg-white/25 text-white' : t.n}`}>{count}</span>
    </button>
  )
}

/* ── attachments ─────────────────────────────────────────────────────────────
 * Staff can send a picture or a document from here. A picture travels as a LINE
 * image message; anything else cannot — the Messaging API has no outbound file
 * type — so the server sends a link instead. See reply.js.
 */

const MAX_FILE_BYTES = 10 * 1024 * 1024

/** Browsers leave `File.type` empty for plenty of ordinary office files, and the
 *  server needs a real content type to check against its whitelist. */
const MIME_BY_EXT = {
  pdf:  'application/pdf',
  doc:  'application/msword',
  docx: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  xls:  'application/vnd.ms-excel',
  xlsx: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  ppt:  'application/vnd.ms-powerpoint',
  pptx: 'application/vnd.openxmlformats-officedocument.presentationml.presentation',
  zip:  'application/zip',
  csv:  'text/csv',
  txt:  'text/plain',
  mp4:  'video/mp4',
  mov:  'video/quicktime',
  mp3:  'audio/mpeg',
  m4a:  'audio/mp4',
}

function mimeFor(file) {
  if (file.type) return file.type
  const ext = (file.name || '').split('.').pop()?.toLowerCase()
  return MIME_BY_EXT[ext] || ''
}

function fileToDataUri(file, mime) {
  // A blank File.type produces a data URI the server cannot parse, so re-wrap
  // the bytes with the type we worked out from the extension.
  const blob = mime && mime !== file.type ? file.slice(0, file.size, mime) : file
  return new Promise((resolve, reject) => {
    const fr = new FileReader()
    fr.onload  = () => resolve(fr.result)
    fr.onerror = () => reject(new Error('อ่านไฟล์ไม่สำเร็จ'))
    fr.readAsDataURL(blob)
  })
}

function fmtSize(bytes) {
  const n = Number(bytes || 0)
  if (!n) return ''
  if (n < 1024) return `${n} B`
  if (n < 1024 * 1024) return `${Math.round(n / 1024)} KB`
  return `${(n / 1024 / 1024).toFixed(1)} MB`
}

/** The transcript stores "[รูปภาพ]" / "[ไฟล์] x.pdf" for a message that was not
 *  words. Hide it only when the real thing is on screen — a "[วิดีโอ] (ไฟล์ใหญ่
 *  เกินเก็บไว้)" row has nothing to show and must keep its text. */
const PLACEHOLDER_RE = /^\[(รูปภาพ|สติกเกอร์|ไฟล์|วิดีโอ|ข้อความเสียง|image|sticker|file|video|audio)\]/

/**
 * LINE hands over sticker ids, never an image, and there is no API that returns
 * one. The stickershop CDN serves it by id; if that ever stops, fall back to the
 * keywords LINE sent with the event, which is the only description there is.
 */
function Sticker({ id, label }) {
  const [broken, setBroken] = useState(false)
  if (broken) return <span>{label || '[สติกเกอร์]'}</span>
  return (
    <img
      src={`https://stickershop.line-scdn.net/stickershop/v1/sticker/${id}/android/sticker.png`}
      alt={label || 'สติกเกอร์'}
      onError={() => setBroken(true)}
      className="h-28 w-28 object-contain"
    />
  )
}

const ATTACHMENT_ICON = { video: Film, audio: Mic }

/** A document, video or voice message in the transcript — click to download. */
function AttachmentChip({ a }) {
  const Icon = ATTACHMENT_ICON[a.kind] || FileText
  return (
    <a href={a.url || undefined} target="_blank" rel="noreferrer"
      className={`mb-1 flex min-w-[180px] items-center gap-2 rounded-lg border border-black/10 bg-white/70 px-2 py-1.5 ${
        a.url ? 'hover:bg-white' : 'pointer-events-none opacity-60'}`}>
      <Icon size={16} className="shrink-0 text-slate-500" />
      <span className="min-w-0 flex-1">
        <span className="block truncate text-xs font-medium text-slate-700">{a.filename}</span>
        <span className="block text-[10px] text-slate-400">{fmtSize(a.size_bytes)}</span>
      </span>
      <Download size={13} className="shrink-0 text-slate-400" />
    </a>
  )
}

export default function BotInbox() {
  const [rows, setRows]       = useState([])
  const [sel, setSel]         = useState(null)
  const [detail, setDetail]   = useState(null)
  const [q, setQ]             = useState('')
  const [filter, setFilter]   = useState('')
  // How many rooms the bot handed off that nobody has claimed. Counted by the
  // server across the whole inbox, NOT from `rows` — the chip has to stay honest
  // while you are looking at a filtered tab.
  const [waiting, setWaiting] = useState(0)
  // Facebook rooms inside the last 4h of the 24h window, or already past it.
  // Server-counted across the whole inbox for the same reason as `waiting`.
  const [fbClosing, setFbClosing] = useState(0)
  // Staff notifications. Until 2026-09-21 these were LINE pushes to a group,
  // billed per member — see functions/api/bot/alerts.js.
  const [alerts, setAlerts]         = useState([])
  const [alertsUnread, setUnread]   = useState(0)
  const [alertsOpen, setAlertsOpen] = useState(false)
  const [channel, setChannel] = useState('')
  const [draft, setDraft]     = useState('')
  const [sending, setSending] = useState(false)
  const [err, setErr]         = useState('')
  const [saved, setSaved]     = useState([])
  const [pickOpen, setPick]   = useState(false)
  const [manageOpen, setMan]  = useState(false)
  const [stickers, setStickers]   = useState([])
  const [stickOpen, setStickOpen] = useState(false)
  const [stickMan, setStickMan]   = useState(false)
  // One attachment per message — the server enforces the same rule, because a
  // transcript row holds one attachment and a second would vanish from the CRM's
  // own record of what the customer was sent. Shapes:
  //   { kind: 'sticker', id, url, title }     one from the sticker library —
  //                                           a REFERENCE; sending uploads nothing
  //   { kind: 'saved', key, url }             an image from a saved reply
  //   { kind: 'image', dataUri, url, name }   a picture just picked
  //   { kind: 'file',  dataUri, name, size }  a document just picked
  const [attach, setAttach]   = useState(null)
  const [dragging, setDrag]   = useState(false)
  const bottomRef = useRef(null)
  const fileRef   = useRef(null)

  async function loadList() {
    try {
      const r = await api.bot.list({
        q: q || undefined,
        // 'fb_closing' is a window filter, not a status — sending it as one
        // would hit the `else if (status)` branch and match no row at all.
        status:  filter === 'fb_closing' ? undefined : (filter || undefined),
        window:  filter === 'fb_closing' ? 'closing' : undefined,
        channel: channel || undefined,
      })
      setRows(r.rows || [])
      setWaiting(r.waiting || 0)
      setFbClosing(r.fb_closing || 0)
    } catch (e) { setErr(e.message) }
  }

  async function loadAlerts() {
    try {
      const r = await api.bot.alerts()
      setAlerts(r.rows || [])
      setUnread(r.unread || 0)
    } catch { /* non-fatal: a missing notification must not break the inbox */ }
  }

  // Stamped read for the whole team at once, deliberately — see alerts.js.
  async function markAlertsRead() {
    setUnread(0)
    try { await api.bot.alertsRead() } catch { /* the badge is already clear */ }
  }

  async function loadDetail(id) {
    if (!id) return
    try {
      setDetail(await api.bot.get(id))
    } catch (e) { setErr(e.message) }
  }

  async function loadSaved() {
    try { setSaved((await api.bot.saved.list()).rows || []) } catch { /* non-fatal */ }
  }
  async function loadStickers() {
    try { setStickers((await api.bot.stickers.list()).rows || []) } catch { /* non-fatal */ }
  }
  useEffect(() => { loadSaved(); loadStickers(); loadAlerts() }, [])
  useEffect(() => { loadList() }, [q, filter, channel])
  useEffect(() => { loadDetail(sel) }, [sel])

  // Poll both the list and the open conversation. A person is reading this
  // screen while a customer is typing, so stale data is worse than a request.
  useEffect(() => {
    const t = setInterval(() => { loadList(); loadDetail(sel); loadAlerts() }, POLL_MS)
    return () => clearInterval(t)
  }, [sel, q, filter, channel])

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' })
  }, [detail?.messages?.length])

  function insertSaved(r) {
    // Text goes into the composer so it can still be edited before sending —
    // a canned reply is a starting point, not a send button.
    if (r.text) setDraft((d) => (d ? `${d}\n${r.text}` : r.text))
    if (r.image_key) setAttach({ kind: 'saved', key: r.image_key, url: r.image_url })
    setPick(false)
  }

  /**
   * Take a File from the picker, a paste or a drop and stage it for sending.
   *
   * Pictures are downscaled to a 1080px JPEG first: LINE is handed one URL for
   * both the full image and its thumbnail, and its thumbnail limit is 1 MB, so
   * an untouched phone photo risks a blank preview in the customer's chat. GIFs
   * skip that path — flattening one to a still would silently drop the animation,
   * so they go as a file link with the animation intact.
   */
  async function pickAttachment(file) {
    if (!file) return
    setErr('')
    try {
      if (file.type.startsWith('image/') && file.type !== 'image/gif') {
        const r = await resizeImageFile(file)
        setAttach({ kind: 'image', dataUri: r.dataUri, url: r.dataUri, name: r.filename, size: r.bytes })
        return
      }
      const mime = mimeFor(file)
      if (!mime) { setErr('ไม่รู้จักชนิดของไฟล์นี้ — ลองบันทึกเป็น PDF ก่อนนะคะ'); return }
      if (file.size > MAX_FILE_BYTES) { setErr(`ไฟล์ใหญ่เกิน 10 MB (${fmtSize(file.size)})`); return }
      setAttach({
        kind: 'file',
        dataUri: await fileToDataUri(file, mime),
        name: file.name || 'file',
        size: file.size,
      })
    } catch (e) {
      setErr(e.message || 'เปิดไฟล์นี้ไม่ได้')
    }
  }

  /**
   * Expand a trailing "/shortcut" if there is one. Returns the new text, or null
   * if nothing matched. Shared by the space path and the Tab path so the two can
   * never drift apart.
   */
  function expandShortcut(v) {
    const m = v.match(/(?:^|\s)\/([\w฀-๿-]{1,32})$/)
    if (!m) return null
    const hit = saved.find((r) => r.shortcut === m[1])
    if (!hit) return null
    if (hit.image_key) setAttach({ kind: 'saved', key: hit.image_key, url: hit.image_url })
    const lead = m[0].startsWith(' ') ? ' ' : ''
    return v.slice(0, v.length - m[0].length) + lead + (hit.text || '')
  }

  // Space triggers it while typing; Tab is handled in onKeyDown, which has to
  // intercept the key before the browser moves focus out of the box.
  function onDraftChange(v) {
    if (v.endsWith(' ')) {
      const expanded = expandShortcut(v.slice(0, -1))
      if (expanded !== null) { setDraft(expanded); return }
    }
    setDraft(v)
  }

  async function send() {
    const text = draft.trim()
    if ((!text && !attach) || sending) return
    setSending(true); setErr('')
    try {
      const payload = { text: text || undefined }
      // The id, not the bytes. The server sends the one stored copy.
      if (attach?.kind === 'sticker') payload.sticker_id = attach.id
      if (attach?.kind === 'saved') payload.image_key  = attach.key
      if (attach?.kind === 'image') { payload.image = attach.dataUri; payload.image_name = attach.name }
      if (attach?.kind === 'file')  payload.file  = { data: attach.dataUri, filename: attach.name }
      await api.bot.send(sel, payload)
      setDraft(''); setAttach(null)
      await Promise.all([loadDetail(sel), loadList()])
    } catch (e) { setErr(e.message) } finally { setSending(false) }
  }

  async function setIgnored(ignored) {
    setErr('')
    try {
      await api.bot.ignore(sel, ignored)
      await Promise.all([loadDetail(sel), loadList()])
    } catch (e) { setErr(e.message) }
  }

  async function setStatus(status) {
    setErr('')
    try {
      await api.bot.status(sel, status)
      await Promise.all([loadDetail(sel), loadList()])
    } catch (e) { setErr(e.message) }
  }

  const session = detail?.session
  const slots   = session?.slots || {}

  const summary = useMemo(() => ([
    ['ผู้ติดต่อ',   slots.contact_name],
    ['บริษัท',      slots.company_name],
    ['เบอร์',       slots.phone],
    ['อีเมล',       slots.email],
    ['จำนวน',       slots.headcount ? `${slots.headcount} คน` : ''],
    ['สถานที่',     slots.location],
    ['ช่วงวันที่',  slots.preferred_dates],
    ['งบประมาณ',    slots.budget],
  ].filter(([, v]) => v)), [detail])

  return (
    <div className="flex h-[calc(100vh-4rem)] gap-4 p-4">
      {/* ── list ─────────────────────────────────────────────── */}
      <div className="flex w-80 shrink-0 flex-col rounded-lg border border-slate-200 bg-white">
        {/* Staff notifications. These were LINE pushes to a group until
            2026-09-21; LINE bills those per member, so an internal alert cost
            five customer-messages in a five-person group and eventually ran the
            quota out. Here they are free, keep their history, and cannot be the
            reason a reply to a customer fails to send. */}
        <div className="border-b border-slate-200 px-3 pt-3">
          <button
            onClick={() => { setAlertsOpen((v) => !v); if (!alertsOpen && alertsUnread) markAlertsRead() }}
            className={`flex w-full items-center gap-2 rounded px-2 py-1.5 text-xs ${
              alertsUnread > 0 ? 'bg-rose-50 text-rose-700' : 'bg-slate-50 text-slate-500'}`}>
            <Bell size={14} />
            <span className="font-medium">แจ้งเตือน</span>
            {alertsUnread > 0 && (
              <span className="rounded-full bg-rose-600 px-1.5 text-[10px] font-semibold text-white">
                {alertsUnread}
              </span>
            )}
            <span className="ml-auto text-slate-400">{alertsOpen ? '▲' : '▼'}</span>
          </button>
          {alertsOpen && (
            <div className="mb-2 mt-1 max-h-56 space-y-1 overflow-y-auto">
              {alerts.length === 0 && (
                <p className="px-2 py-3 text-xs text-slate-400">ยังไม่มีแจ้งเตือน</p>
              )}
              {alerts.map((a) => (
                <button key={a.id}
                  onClick={() => { if (a.session_id) setSel(a.session_id) }}
                  className={`block w-full rounded border p-2 text-left text-[11px] leading-relaxed ${
                    a.read_at ? 'border-slate-200 bg-white text-slate-500'
                              : 'border-rose-200 bg-rose-50 text-slate-700'}`}>
                  {/* whitespace-pre-line: formatAlert() writes one field per
                      line and that shape is what makes it scannable. */}
                  <span className="block whitespace-pre-line">{a.text}</span>
                  <span className="mt-1 block text-[10px] text-slate-400">{timeAgo(a.created_at)}</span>
                </button>
              ))}
            </div>
          )}
        </div>
        <div className="border-b border-slate-200 p-3">
          <div className="relative">
            <Search size={14} className="absolute left-2 top-2.5 text-slate-400" />
            <input
              value={q} onChange={(e) => setQ(e.target.value)}
              placeholder="ค้นหาชื่อ หรือบริษัท"
              className="w-full rounded border border-slate-300 py-1.5 pl-7 pr-2 text-sm"
            />
          </div>
          {/* TWO DIMENSIONS, TWO LABELLED CONTROLS.
              This was nine identical chips across two rows — status on the first,
              channel on the second — and nothing on screen said they were
              different questions. "ปิดแล้ว" and "Facebook" looked like
              alternatives to each other, so people picked one expecting it to
              replace the other and got an empty list. A dropdown that names its
              own dimension cannot be misread that way, and it costs two lines
              instead of four in the narrowest column in the app. */}
          <div className="mt-2 grid grid-cols-2 gap-1.5">
            <select value={filter} onChange={(e) => setFilter(e.target.value)} className={FILTER_SEL}>
              <option value="">สถานะ · ทั้งหมด</option>
              <option value="waiting">รอเจ้าหน้าที่{waiting > 0 ? ` (${waiting})` : ''}</option>
              <option value="bot">บอทกำลังตอบ</option>
              <option value="human">เจ้าหน้าที่ดูแล</option>
              <option value="done">ปิดแล้ว</option>
              {/* Kept in the list while it is the current choice even at zero,
                  or the select would fall back to showing สถานะ · ทั้งหมด while
                  the list stays filtered. */}
              {(fbClosing > 0 || filter === 'fb_closing') && (
                <option value="fb_closing">ใกล้หมดเวลา ({fbClosing})</option>
              )}
            </select>
            <select value={channel} onChange={(e) => setChannel(e.target.value)} className={FILTER_SEL}>
              <option value="">ช่องทาง · ทั้งหมด</option>
              <option value="line">LINE</option>
              <option value="facebook">Facebook</option>
            </select>
          </div>
          {/* The backlog rides on these so an unattended room is visible without
              opening the dropdown — and without reordering the list, which staff
              read as newest-first. */}
          {(waiting > 0 || fbClosing > 0) && (
            <div className="mt-1.5 flex gap-1.5">
              {waiting > 0 && (
                <UrgentChip tone="rose" icon={AlertCircle} label="รอเจ้าหน้าที่" count={waiting}
                  active={filter === 'waiting'}
                  onClick={() => setFilter(filter === 'waiting' ? '' : 'waiting')} />
              )}
              {fbClosing > 0 && (
                <UrgentChip tone="amber" icon={Clock} label="ใกล้หมดเวลา" count={fbClosing}
                  active={filter === 'fb_closing'}
                  onClick={() => setFilter(filter === 'fb_closing' ? '' : 'fb_closing')} />
              )}
            </div>
          )}
        </div>

        <div className="flex-1 overflow-y-auto">
          {rows.length === 0 && (
            <p className="p-4 text-sm text-slate-400">ยังไม่มีบทสนทนา</p>
          )}
          {rows.map((r) => {
            // The GET stamps last_read_at, but the list is on its own 15s clock,
            // so a freshly-opened row can still come back unread. Whatever is on
            // screen is read — decide it here rather than racing the two polls.
            const unread = !!r.unread && r.id !== sel
            return (
            <button key={r.id} onClick={() => setSel(r.id)}
              className={`block w-full border-b border-slate-100 p-3 text-left hover:bg-slate-50 ${sel === r.id ? 'bg-slate-50' : ''}`}>
              <div className="flex items-center justify-between gap-2">
                <span className={`truncate text-sm ${unread ? 'font-bold text-slate-900' : 'font-medium text-slate-800'}`}>
                  {r.title}
                </span>
                <span className="flex shrink-0 items-center gap-1.5">
                  <span className="text-[11px] text-slate-400">{timeAgo(r.last_message_at)}</span>
                  {/* The green dot: the customer has written since anyone last
                      opened this conversation. Same meaning as in LINE. */}
                  {unread && (
                    <span title="ยังไม่ได้อ่าน"
                      className="h-2.5 w-2.5 shrink-0 rounded-full bg-[#06C755]" />
                  )}
                </span>
              </div>
              {r.subtitle && <div className="truncate text-xs text-slate-500">{r.subtitle}</div>}
              {/* ONE LINE, NEVER TWO. Every badge here is shrink-0 and
                  whitespace-nowrap, and the lead number is pushed to the far
                  right — so a room with a channel, a status, a Facebook clock
                  and a lead still reads as a single strip instead of wrapping
                  into the two-tall block this was. */}
              <div className="mt-1 flex items-center gap-1.5 overflow-hidden">
                <ChannelBadge channel={r.channel} />
                {r.ignored
                  ? <span title="ปิดบอทถาวร"
                      className="inline-flex shrink-0 items-center gap-1 whitespace-nowrap rounded bg-rose-100 px-1.5 text-[10px] font-medium leading-[18px] text-rose-700">
                      🔇 ปิดบอท
                    </span>
                  : <StatusPill status={r.status} takenOverBy={r.taken_over_by} />}
                <FbWindowBadge win={r.fb_window} />
                {!!r.lead_id && (
                  <span className="ml-auto shrink-0 text-[10px] tabular-nums text-slate-400">#{r.lead_id}</span>
                )}
              </div>
              {r.last_text && (
                <p className={`mt-1 truncate text-xs ${unread ? 'font-medium text-slate-600' : 'text-slate-400'}`}>
                  {r.last_role === 'user' ? '' : r.last_role === 'staff' ? '↩ ' : '🤖 '}{r.last_text}
                </p>
              )}
            </button>
            )
          })}
        </div>
      </div>

      {/* ── conversation ─────────────────────────────────────── */}
      <div
        className={`relative flex flex-1 flex-col rounded-lg border bg-white ${
          dragging ? 'border-sky-400 ring-2 ring-sky-200' : 'border-slate-200'}`}
        onDragOver={(e) => { if (session) { e.preventDefault(); setDrag(true) } }}
        onDragLeave={(e) => {
          // Only when the pointer actually leaves the panel — dragging across a
          // child fires dragleave for the child and would flicker the overlay.
          if (!e.currentTarget.contains(e.relatedTarget)) setDrag(false)
        }}
        onDrop={(e) => {
          if (!session) return
          e.preventDefault(); setDrag(false)
          pickAttachment(e.dataTransfer?.files?.[0])
        }}
      >
        {dragging && (
          <div className="pointer-events-none absolute inset-0 z-10 flex items-center justify-center rounded-lg bg-sky-50/80 text-sm font-medium text-sky-700">
            วางไฟล์ที่นี่เพื่อแนบ
          </div>
        )}
        {!session && (
          <div className="flex flex-1 items-center justify-center text-sm text-slate-400">
            เลือกบทสนทนาทางซ้าย
          </div>
        )}

        {session && (
          <>
            <div className="flex items-center justify-between gap-3 border-b border-slate-200 p-3">
              <div className="min-w-0">
                <div className="truncate font-medium text-slate-800">
                  {session.display_name || slots.contact_name || session.external_id}
                </div>
                <div className="flex items-center gap-2 text-xs text-slate-500">
                  <ChannelBadge channel={session.channel} size="md" />
                  <StatusPill status={session.status} takenOverBy={session.taken_over_by} size="md" />
                  {session.taken_over_by && <span>โดย {session.taken_over_by}</span>}
                  {!!session.lead_id && (
                    <a href={`/leads?id=${session.lead_id}`} className="inline-flex items-center gap-1 text-sky-600 hover:underline">
                      Lead #{session.lead_id} <ExternalLink size={11} />
                    </a>
                  )}
                </div>
              </div>
              <div className="flex shrink-0 gap-2">
                {session.status !== 'bot' && (
                  <button onClick={() => setStatus('bot')}
                    className="rounded border border-emerald-300 px-3 py-1.5 text-sm text-emerald-700 hover:bg-emerald-50">
                    ให้บอทคุยต่อ
                  </button>
                )}
                {session.status === 'bot' && (
                  <button onClick={() => setStatus('human')}
                    className="rounded border border-amber-300 px-3 py-1.5 text-sm text-amber-700 hover:bg-amber-50">
                    รับช่วงต่อเอง
                  </button>
                )}
                {session.status !== 'done' && (
                  <button onClick={() => setStatus('done')}
                    className="rounded border border-slate-300 px-3 py-1.5 text-sm text-slate-600 hover:bg-slate-50">
                    ปิด
                  </button>
                )}
                <button onClick={() => setIgnored(!session.ignored)}
                  /* `ignored` is 0/1 from SQLite, so compare explicitly below */
                  title={session.ignored ? 'ให้บอทกลับมาตอบห้องนี้อีกครั้ง' : 'ห้องนี้ไม่ใช่ลูกค้า — ปิดบอทถาวร ไม่ให้บอทอ่านหรือตอบอีกเลย'}
                  className={`rounded border px-3 py-1.5 text-sm ${session.ignored
                    ? 'border-rose-300 bg-rose-50 text-rose-700'
                    : 'border-slate-300 text-slate-600 hover:bg-slate-50'}`}>
                  {session.ignored ? 'เปิดบอทอีกครั้ง' : 'ปิดบอทถาวร'}
                </button>
                <button onClick={() => { loadList(); loadDetail(sel) }}
                  className="rounded border border-slate-300 p-1.5 text-slate-500 hover:bg-slate-50" title="รีเฟรช">
                  <RefreshCw size={14} />
                </button>
              </div>
            </div>

            {!!session.ignored && (
              <div className="border-b border-rose-200 bg-rose-50 px-3 py-2 text-xs text-rose-700">
                🔇 ห้องนี้ปิดบอทถาวร — บอทจะไม่อ่านและไม่ตอบข้อความที่เข้ามาอีกเลย จนกว่าจะกด "เปิดบอทอีกครั้ง"
                (ใช้กับ contact ที่ไม่ใช่ลูกค้า เช่น ช่องแจ้งเตือนฟอร์มเว็บไซต์ · ถ้าแค่อยากตอบเองรอบนี้ ให้ใช้ "รับช่วงต่อเอง" แทน)
              </div>
            )}

            {summary.length > 0 && (
              <div className="flex flex-wrap gap-x-4 gap-y-1 border-b border-slate-100 bg-slate-50 px-3 py-2 text-xs text-slate-600">
                {summary.map(([k, v]) => (
                  <span key={k}><span className="text-slate-400">{k}:</span> {v}</span>
                ))}
              </div>
            )}

            <div className="flex-1 space-y-2 overflow-y-auto p-4">
              {(detail?.messages || []).map((m, i, arr) => {
                const mine = m.role !== 'user'
                // A gap this long means the server rolled the session over and
                // what follows is a separate enquiry — mark it so the two are
                // not read as one conversation.
                const gap = i > 0 && (m.created_at - arr[i - 1].created_at) > 30 * 86400
                // Hide the "[รูปภาพ]" style placeholder only when the thing it
                // stands for is actually on screen.
                const media    = !!(m.sticker_id || m.our_sticker || m.image_url || m.attachment)
                const showText = m.text && !(media && PLACEHOLDER_RE.test(m.text))
                return (
                  <div key={m.id}>
                  {gap && (
                    <div className="my-3 flex items-center gap-2 text-[11px] text-slate-400">
                      <span className="h-px flex-1 bg-slate-200" />
                      บทสนทนาใหม่ · {clock(m.created_at)}
                      <span className="h-px flex-1 bg-slate-200" />
                    </div>
                  )}
                  <div className={`flex ${mine ? 'justify-end' : 'justify-start'}`}>
                    <div className={`max-w-[70%] rounded-2xl px-3 py-2 text-sm whitespace-pre-wrap ${
                      m.role === 'user'  ? 'bg-slate-100 text-slate-800'
                    : m.role === 'staff' ? 'bg-amber-100 text-amber-900'
                    :                      'bg-emerald-50 text-emerald-900'}`}>
                      {/* m.sticker_id is LINE's — one the CUSTOMER sent us.
                          m.our_sticker is one of OURS that went out. Different
                          columns, deliberately different names (migration 066). */}
                      {m.sticker_id && <Sticker id={m.sticker_id} label={m.text} />}
                      {m.our_sticker?.url && (
                        <img src={m.our_sticker.url} alt={m.our_sticker.title || 'สติกเกอร์'}
                          title={m.our_sticker.title || ''}
                          className="h-28 w-28 object-contain" />
                      )}
                      {m.image_url && (
                        <a href={m.image_url} target="_blank" rel="noreferrer">
                          <img src={m.image_url} alt=""
                            className="mb-1 max-h-56 rounded-lg object-cover" />
                        </a>
                      )}
                      {m.attachment && <AttachmentChip a={m.attachment} />}
                      {showText && m.text}
                      <div className="mt-1 text-[10px] opacity-60">
                        {m.role === 'staff' ? `${m.staff_name || 'เจ้าหน้าที่'} · ` : m.role === 'bot' ? 'บอท · ' : ''}
                        {clock(m.created_at)}
                      </div>
                    </div>
                  </div>
                  </div>
                )
              })}
              <div ref={bottomRef} />
            </div>

            {/* whitespace-pre-line: a send failure explains itself over two lines
                — what went wrong, then what to do instead. */}
            {err && <p className="px-4 pb-1 text-xs text-rose-600 whitespace-pre-line">{err}</p>}

            {/* Said BEFORE the message is typed, not after it fails. The whole
                point of the badge in the list is that someone acts while there
                is still time; repeating it over the composer is the last place
                that can still happen. */}
            {detail?.session?.fb_window?.state === 'closing' && (
              <p className="mx-3 mb-2 flex items-start gap-1.5 rounded border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800">
                <Clock size={14} className="mt-0.5 shrink-0" />
                <span>
                  เหลือเวลาตอบกลับทาง Facebook อีกประมาณ <b>{detail.session.fb_window.hours_left} ชั่วโมง</b> —
                  หลังจากนั้นจะส่งข้อความไม่ได้จนกว่าลูกค้าจะทักเข้ามาใหม่
                </span>
              </p>
            )}
            {detail?.session?.fb_window?.state === 'closed' && (
              <p className="mx-3 mb-2 flex items-start gap-1.5 rounded border border-rose-200 bg-rose-50 px-3 py-2 text-xs text-rose-700">
                <AlertCircle size={14} className="mt-0.5 shrink-0" />
                <span>
                  <b>เกิน 24 ชั่วโมงนับจากข้อความล่าสุดของลูกค้าแล้ว</b> — Facebook ปิดหน้าต่างการตอบกลับ
                  ระบบจะลองส่งด้วยสิทธิ์ Human Agent ให้ แต่ถ้าแอปยังไม่ได้รับอนุมัติจะส่งไม่สำเร็จ
                  แนะนำให้โทรหาลูกค้าแทน
                </span>
              </p>
            )}

            <div className="border-t border-slate-200 p-3">
              {session.status === 'bot' && (
                <p className="mb-2 text-xs text-amber-700">
                  บอทกำลังดูแลห้องนี้อยู่ — ถ้าพิมพ์ตอบ ระบบจะเปลี่ยนเป็น "เจ้าหน้าที่ดูแล" ให้อัตโนมัติ
                </p>
              )}
              <div className="mb-2 flex items-center gap-2">
                <button onClick={() => setPick((v) => !v)}
                  title="ข้อความสำเร็จรูป — หรือพิมพ์ /ชื่อย่อ แล้วเว้นวรรคหรือกด Tab ในช่องข้อความ"
                  className="inline-flex items-center gap-1 rounded border border-slate-300 px-2 py-1 text-xs text-slate-600 hover:bg-slate-50">
                  <Zap size={12} /> ข้อความสำเร็จรูป
                </button>
                <button onClick={() => fileRef.current?.click()}
                  title="แนบรูปหรือไฟล์ — ลากมาวางหรือวาง (paste) ก็ได้"
                  className="inline-flex items-center gap-1 rounded border border-slate-300 px-2 py-1 text-xs text-slate-600 hover:bg-slate-50">
                  <Paperclip size={12} /> แนบรูป/ไฟล์
                </button>
                <input ref={fileRef} type="file" className="hidden"
                  accept="image/*,application/pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.zip,.csv,.txt,video/mp4,audio/mpeg,audio/mp4"
                  onChange={(e) => { pickAttachment(e.target.files?.[0]); e.target.value = '' }} />
                <button onClick={() => { setStickOpen((v) => !v); setPick(false) }}
                  title="ส่งสติกเกอร์ — เก็บไว้ชุดเดียว ส่งกี่ครั้งก็ไม่กินที่เพิ่ม"
                  className="inline-flex items-center gap-1 rounded border border-slate-300 px-2 py-1 text-xs text-slate-600 hover:bg-slate-50">
                  <Smile size={12} /> สติกเกอร์
                </button>
                <button onClick={() => setMan(true)}
                  className="text-xs text-slate-400 hover:text-slate-600">จัดการ</button>
              </div>

              {/* Show the actual picture, not the word "image" — the whole point of
                  a preview is catching the wrong one before the customer sees it. */}
              {attach && (
                <div className="mb-2 flex items-center gap-2">
                  <div className="relative">
                    {attach.kind === 'file'
                      ? (
                        <div className="flex h-16 min-w-[180px] items-center gap-2 rounded-lg border border-slate-200 px-3">
                          <FileText size={18} className="shrink-0 text-slate-400" />
                          <span className="min-w-0">
                            <span className="block max-w-[220px] truncate text-xs font-medium text-slate-700">{attach.name}</span>
                            <span className="block text-[10px] text-slate-400">{fmtSize(attach.size)}</span>
                          </span>
                        </div>
                      )
                      : <img src={attach.url} alt={attach.title || 'แนบรูป'}
                          className={`h-16 w-16 rounded-lg border border-slate-200 ${
                            attach.kind === 'sticker' ? 'object-contain p-1' : 'object-cover'}`} />}
                    <button onClick={() => setAttach(null)} title="เอาไฟล์ออก"
                      className="absolute -right-1.5 -top-1.5 rounded-full bg-slate-800 p-0.5 text-white shadow hover:bg-rose-600">
                      <X size={12} />
                    </button>
                  </div>
                  <span className="text-xs text-slate-400">
                    {attach.kind === 'file'
                      // Worth saying plainly: LINE has no file message, so what
                      // the customer receives is a link, not the document itself.
                      ? 'ส่งเป็นลิงก์ดาวน์โหลดให้ลูกค้า (LINE ส่งไฟล์ตรงๆ ไม่ได้)'
                      : attach.kind === 'sticker'
                        ? `สติกเกอร์: ${attach.title || ''}`
                        : 'แนบรูปนี้ไปด้วย'}
                  </span>
                </div>
              )}

              {stickOpen && (
                <div className="mb-2 max-h-48 overflow-y-auto rounded border border-slate-200 p-2">
                  <div className="mb-2 flex items-center justify-between">
                    <span className="text-[11px] text-slate-400">
                      {stickers.length ? `${stickers.length} สติกเกอร์` : 'ยังไม่มีสติกเกอร์'}
                    </span>
                    <button onClick={() => { setStickMan(true); setStickOpen(false) }}
                      className="text-[11px] text-sky-600 hover:underline">อัปโหลด / จัดการสติกเกอร์</button>
                  </div>
                  <div className="grid grid-cols-6 gap-2">
                    {stickers.map((st) => (
                      <button key={st.id} title={`${st.title}${st.meaning ? ` — ${st.meaning}` : ''}`}
                        onClick={() => {
                          setAttach({ kind: 'sticker', id: st.id, url: st.url, title: st.title })
                          setStickOpen(false)
                        }}
                        className="aspect-square rounded-lg border border-slate-200 p-1 hover:border-sky-400 hover:bg-sky-50">
                        <img src={st.url} alt={st.title} className="h-full w-full object-contain" />
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {pickOpen && (
                <div className="mb-2 max-h-48 overflow-y-auto rounded border border-slate-200">
                  {saved.length === 0 && (
                    <p className="p-3 text-xs text-slate-400">ยังไม่มีข้อความสำเร็จรูป — กด "จัดการ" เพื่อเพิ่ม</p>
                  )}
                  {saved.map((r) => (
                    <button key={r.id} onClick={() => insertSaved(r)}
                      className="flex w-full items-start gap-2 border-b border-slate-100 p-2 text-left hover:bg-slate-50">
                      {r.image_url && <img src={r.image_url} alt="" className="h-12 w-12 shrink-0 rounded border border-slate-200 object-cover" />}
                      <span className="min-w-0">
                        <span className="block text-sm font-semibold text-slate-800">/{r.shortcut}</span>
                        <span className="block truncate text-[11px] text-slate-500">{r.title}</span>
                        {r.text && <span className="block truncate text-[11px] text-slate-400">{r.text}</span>}
                      </span>
                    </button>
                  ))}
                </div>
              )}

              <div className="flex gap-2">
                <textarea
                  value={draft} onChange={(e) => onDraftChange(e.target.value)}
                  onPaste={(e) => {
                    // A screenshot on the clipboard is the fastest way staff
                    // answer "ส่งรูปตัวอย่างหน่อย" — take it before the textarea
                    // turns it into nothing.
                    const f = e.clipboardData?.files?.[0]
                    if (f) { e.preventDefault(); pickAttachment(f) }
                  }}
                  onKeyDown={(e) => {
                    // Tab must be caught here or the browser moves focus away
                    // before the shortcut can expand.
                    if (e.key === 'Tab') {
                      const expanded = expandShortcut(draft)
                      if (expanded !== null) { e.preventDefault(); setDraft(expanded) }
                      return
                    }
                    if (e.key !== 'Enter') return
                    // Enter sends; hold a modifier for a new line.
                    if (e.metaKey || e.ctrlKey || e.shiftKey) {
                      e.preventDefault()
                      const el = e.target
                      const at = el.selectionStart ?? draft.length
                      const next = draft.slice(0, at) + '\n' + draft.slice(el.selectionEnd ?? at)
                      setDraft(next)
                      requestAnimationFrame(() => { el.selectionStart = el.selectionEnd = at + 1 })
                      return
                    }
                    e.preventDefault()
                    send()
                  }}
                  rows={2} placeholder="พิมพ์ข้อความ… Enter เพื่อส่ง"
                  title={'Enter เพื่อส่ง\nCtrl/⇧ + Enter ขึ้นบรรทัดใหม่\n/ชื่อย่อ แล้วเว้นวรรคหรือ Tab เพื่อแทรกข้อความสำเร็จรูป\nวางหรือลากรูป/ไฟล์มาแนบได้เลย'}
                  className="flex-1 resize-none rounded border border-slate-300 p-2 text-sm"
                />
                <button onClick={send} disabled={sending || (!draft.trim() && !attach)}
                  className="flex items-center gap-1 rounded bg-slate-800 px-4 text-sm text-white disabled:opacity-40">
                  <Send size={14} />{sending ? 'กำลังส่ง' : 'ส่ง'}
                </button>
              </div>
            </div>
          </>
        )}
      </div>
      {manageOpen && (
        <SavedReplyManager
          rows={saved}
          onClose={() => { setMan(false); loadSaved() }}
        />
      )}

      {stickMan && (
        <StickerManager
          rows={stickers}
          onClose={() => { setStickMan(false); loadStickers() }}
        />
      )}
    </div>
  )
}

/**
 * Upload and curate the sticker library.
 *
 * WHY UPLOADING HAPPENS IN THE BROWSER: the resize has to run somewhere, and the
 * browser already has a canvas. Doing it here means no image library in the
 * Worker, no Wasabi credentials in a script on anyone's laptop, and staff can add
 * a sticker without a developer. The file that leaves this page is already the
 * 320px PNG that will be stored.
 *
 * ⚠️ PNG, NOT JPEG, and 320px NOT 1080px. JPEG has no alpha — a transparent
 * sticker would arrive in a white box — and 320 is what LINE's own stickers are,
 * which is what makes an image message read as a sticker instead of a photo.
 *
 * `meaning` is the only field the BOT ever sees. Write what the sticker is FOR
 * ("ขอบคุณ ปิดท้ายบทสนทนา") rather than what it shows ("ฟันยิ้มถือแปรง") — the model
 * is picking a moment, not a picture. A sticker with no meaning is invisible to
 * the bot and stays staff-only, which is a perfectly good way to park one.
 */
function StickerManager({ rows, onClose }) {
  const [list, setList] = useState(rows)
  const [busy, setBusy] = useState(false)
  const [err, setErr]   = useState('')
  const [note, setNote] = useState('')
  const fileRef = useRef(null)

  async function refresh() {
    try { setList((await api.bot.stickers.list()).rows || []) } catch { /* keep */ }
  }

  async function upload(files) {
    const picked = [...(files || [])].filter((f) => /^image\/(png|jpeg)$/.test(f.type))
    if (!picked.length) { setErr('รองรับเฉพาะ PNG และ JPEG'); return }
    setBusy(true); setErr(''); setNote('')

    let added = 0, duped = 0
    for (const file of picked) {
      try {
        const r = await resizeImageFile(file, { maxDim: STICKER_DIM, format: 'png' })
        const res = await api.bot.stickers.create({
          data: r.dataUri,
          // The filename is the only name we have; staff rename afterwards.
          title: (file.name || 'sticker').replace(/\.[^.]+$/, '').slice(0, 60),
          width: r.width, height: r.height,
        })
        if (res.deduped) duped++; else added++
      } catch (e) {
        setErr(`${file.name}: ${e.message}`)
      }
    }
    await refresh()
    setBusy(false)
    // Say when nothing was stored, and why. Silence after dropping twelve files
    // looks like a failure even when the dedupe did exactly its job.
    setNote(duped
      ? `เพิ่ม ${added} · ข้าม ${duped} (มีอยู่แล้ว ไม่ได้เก็บไฟล์ซ้ำ)`
      : `เพิ่ม ${added} สติกเกอร์`)
  }

  async function patch(id, field, value) {
    try {
      await api.bot.stickers.update(id, { [field]: value })
      setList((l) => l.map((x) => (x.id === id ? { ...x, [field]: value } : x)))
    } catch (e) { setErr(e.message) }
  }

  async function remove(st) {
    try {
      const r = await api.bot.stickers.remove(st.id)
      setNote(r.archived
        ? `เก็บ "${st.title}" เข้าคลัง (เคยส่งไปแล้ว จึงลบถาวรไม่ได้ ประวัติแชทจะได้ไม่หาย)`
        : `ลบ "${st.title}" แล้ว`)
      await refresh()
    } catch (e) { setErr(e.message) }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      <div className="flex max-h-[85vh] w-full max-w-3xl flex-col rounded-lg bg-white shadow-xl">
        <div className="flex items-center justify-between border-b border-slate-200 p-3">
          <h2 className="font-medium text-slate-800">สติกเกอร์</h2>
          <button onClick={onClose} className="rounded p-1 text-slate-400 hover:bg-slate-100">
            <X size={18} />
          </button>
        </div>

        <div className="border-b border-slate-200 p-3">
          <div
            onDragOver={(e) => e.preventDefault()}
            onDrop={(e) => { e.preventDefault(); upload(e.dataTransfer?.files) }}
            className="rounded-lg border-2 border-dashed border-slate-300 p-4 text-center">
            <p className="text-sm text-slate-600">ลากไฟล์ PNG มาวางที่นี่ หรือ</p>
            <button onClick={() => fileRef.current?.click()} disabled={busy}
              className="mt-2 rounded bg-slate-800 px-3 py-1.5 text-sm text-white disabled:opacity-50">
              {busy ? 'กำลังอัปโหลด…' : 'เลือกไฟล์'}
            </button>
            <input ref={fileRef} type="file" accept="image/png,image/jpeg" multiple className="hidden"
              onChange={(e) => { upload(e.target.files); e.target.value = '' }} />
            <p className="mt-2 text-[11px] text-slate-400">
              ย่อเป็น PNG {STICKER_DIM}px ให้อัตโนมัติ · ไฟล์ซ้ำจะไม่ถูกเก็บสองครั้ง
            </p>
          </div>
          {err  && <p className="mt-2 text-xs text-rose-600">{err}</p>}
          {note && <p className="mt-2 text-xs text-emerald-700">{note}</p>}
        </div>

        <div className="flex-1 overflow-y-auto p-3">
          {list.length === 0 && <p className="text-sm text-slate-400">ยังไม่มีสติกเกอร์</p>}
          <div className="space-y-2">
            {list.map((st) => (
              <div key={st.id} className="flex items-start gap-3 rounded-lg border border-slate-200 p-2">
                <img src={st.url} alt={st.title}
                  className="h-16 w-16 shrink-0 rounded border border-slate-100 object-contain" />
                <div className="min-w-0 flex-1 space-y-1">
                  <input defaultValue={st.title} placeholder="ชื่อ"
                    onBlur={(e) => e.target.value.trim() !== st.title && patch(st.id, 'title', e.target.value.trim())}
                    className="w-full rounded border border-slate-200 px-2 py-1 text-sm" />
                  <input defaultValue={st.meaning} placeholder="ใช้ตอนไหน — บอทอ่านช่องนี้ เช่น ขอบคุณ / ทักทาย / ขอโทษที่ตอบช้า"
                    onBlur={(e) => e.target.value.trim() !== st.meaning && patch(st.id, 'meaning', e.target.value.trim())}
                    className="w-full rounded border border-slate-200 px-2 py-1 text-xs" />
                  <div className="flex items-center gap-3 pt-0.5">
                    <label className="flex items-center gap-1 text-[11px] text-slate-500">
                      <input type="checkbox" checked={!!st.bot_usable}
                        onChange={(e) => patch(st.id, 'bot_usable', e.target.checked)} />
                      ให้บอทเลือกใช้ได้
                    </label>
                    <span className="text-[11px] text-slate-300">{st.slug}</span>
                    {!st.meaning && !!st.bot_usable && (
                      <span className="text-[11px] text-amber-600">ยังไม่ได้ใส่ว่าใช้ตอนไหน บอทจะไม่เลือกอันนี้</span>
                    )}
                  </div>
                </div>
                <button onClick={() => remove(st)} title="ลบ"
                  className="rounded p-1 text-slate-300 hover:bg-rose-50 hover:text-rose-600">
                  <Trash2 size={15} />
                </button>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  )
}

/** Add / edit / delete saved replies. Images are optional; text is optional too,
 *  but not both — the server rejects a reply that would send nothing. */
function SavedReplyManager({ rows, onClose }) {
  const [list, setList]   = useState(rows)
  const [form, setForm]   = useState({ shortcut: '', title: '', text: '', image: null, preview: null })
  const [busy, setBusy]   = useState(false)
  const [err, setErr]     = useState('')

  async function refresh() {
    try { setList((await api.bot.saved.list()).rows || []) } catch { /* keep */ }
  }

  function pickFile(file) {
    if (!file) return
    if (!/^image\/(jpeg|png)$/.test(file.type)) { setErr('รองรับเฉพาะ JPEG และ PNG'); return }
    if (file.size > 4 * 1024 * 1024)            { setErr('ไฟล์ใหญ่เกิน 4 MB'); return }
    const fr = new FileReader()
    fr.onload = () => setForm((f) => ({ ...f, image: fr.result, preview: fr.result }))
    fr.readAsDataURL(file)
  }

  async function create() {
    setErr('')
    if (!form.shortcut.trim() || !form.title.trim()) { setErr('ต้องมีชื่อย่อและชื่อเรื่อง'); return }
    if (!form.text.trim() && !form.image)            { setErr('ต้องมีข้อความ หรือรูปภาพ อย่างน้อยหนึ่งอย่าง'); return }
    setBusy(true)
    try {
      await api.bot.saved.create({
        shortcut: form.shortcut.trim(), title: form.title.trim(),
        text: form.text.trim(), image: form.image || undefined,
      })
      setForm({ shortcut: '', title: '', text: '', image: null, preview: null })
      await refresh()
    } catch (e) { setErr(e.message) } finally { setBusy(false) }
  }

  async function remove(id) {
    setErr('')
    try { await api.bot.saved.remove(id); await refresh() } catch (e) { setErr(e.message) }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" onClick={onClose}>
      <div className="max-h-[85vh] w-full max-w-2xl overflow-y-auto rounded-lg bg-white p-5"
           onClick={(e) => e.stopPropagation()}>
        <div className="mb-4 flex items-center justify-between">
          <h2 className="text-lg font-semibold text-slate-800">ข้อความสำเร็จรูป</h2>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-700"><X size={18} /></button>
        </div>

        <div className="mb-5 space-y-2 rounded border border-slate-200 p-3">
          <div className="flex gap-2">
            <input value={form.shortcut} onChange={(e) => setForm({ ...form, shortcut: e.target.value })}
              placeholder="ชื่อย่อ เช่น ปกส" className="w-40 rounded border border-slate-300 px-2 py-1.5 text-sm" />
            <input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })}
              placeholder="ชื่อเรื่อง เช่น อธิบายสิทธิประกันสังคม"
              className="flex-1 rounded border border-slate-300 px-2 py-1.5 text-sm" />
          </div>
          <textarea value={form.text} onChange={(e) => setForm({ ...form, text: e.target.value })}
            rows={3} placeholder="ข้อความ (เว้นว่างได้ถ้าส่งเฉพาะรูป)"
            className="w-full resize-none rounded border border-slate-300 p-2 text-sm" />
          <div className="flex items-center gap-3">
            <label className="cursor-pointer rounded border border-slate-300 px-2 py-1 text-xs text-slate-600 hover:bg-slate-50">
              เลือกรูป (JPEG/PNG)
              <input type="file" accept="image/jpeg,image/png" className="hidden"
                onChange={(e) => pickFile(e.target.files?.[0])} />
            </label>
            {form.preview && (
              <span className="flex items-center gap-2">
                <img src={form.preview} alt="" className="h-10 w-10 rounded object-cover" />
                <button onClick={() => setForm({ ...form, image: null, preview: null })}
                  className="text-xs text-slate-400 hover:text-rose-600">เอาออก</button>
              </span>
            )}
            <button onClick={create} disabled={busy}
              className="ml-auto inline-flex items-center gap-1 rounded bg-slate-800 px-3 py-1.5 text-sm text-white disabled:opacity-40">
              <Plus size={14} />{busy ? 'กำลังบันทึก' : 'เพิ่ม'}
            </button>
          </div>
          {err && <p className="text-xs text-rose-600">{err}</p>}
        </div>

        <div className="divide-y divide-slate-100">
          {list.length === 0 && <p className="py-6 text-center text-sm text-slate-400">ยังไม่มีข้อความสำเร็จรูป</p>}
          {list.map((r) => (
            <div key={r.id} className="flex items-start gap-3 py-2">
              {r.image_url && <img src={r.image_url} alt="" className="h-10 w-10 shrink-0 rounded object-cover" />}
              <div className="min-w-0 flex-1">
                <div className="text-sm font-semibold text-slate-800">/{r.shortcut}</div>
                <div className="text-[11px] text-slate-500">{r.title}</div>
                {r.text && <p className="mt-0.5 whitespace-pre-wrap text-xs text-slate-400">{r.text}</p>}
              </div>
              <button onClick={() => remove(r.id)} className="text-slate-300 hover:text-rose-600" title="ลบ">
                <Trash2 size={15} />
              </button>
            </div>
          ))}
        </div>
      </div>
    </div>
  )
}
